<template>
  <v-row v-if="tab === 1" class="import-section">
    <v-col cols="8">
      <div class="csv-import-section d-flex justify-center align-content-center flex-wrap">
        <mf-button text color="marca-0-l40" :disabled="disabled" label="Importar lista de produtos" icon="cloud_upload" @click="openImportModal = true" />

        <div>
          <p class="label-csv-import text-center">Somente formato csv</p>
        </div>
      </div>
      <import-dynamic-csv
        v-model="openImportModal"
        title="Importar produtos"
        import-label="Importar lista de produtos"
        load-objects
        :required-any-of="importRequiredAnyOfFields"
        :fields="importFields"
      />
    </v-col>
    <v-col class="selected-products-section" cols="4">
      <selected-campaign-items :campaign="campaign" :disabled="disabled" @set-item="setItem" @remove-group="removeGroup" />
    </v-col>
  </v-row>
</template>

<script>
import { flatten, cloneDeep, find, findIndex } from 'lodash'

import ImportDynamicCsv from '@/components/ImportDynamicCsv.vue'
import SelectedCampaignItems from '@/modules/maria/campanha-da-maria/views/components/SelectedCampaignItems.vue'
import { QUERY_VALIDATE_IMPORTED_PRODUCTS } from '@/modules/campanhas/gestao-ofertas-v2/graphql'

export default {
  name: 'FormMariaCampaignTab2',

  components: {
    ImportDynamicCsv,
    SelectedCampaignItems,
  },

  props: {
    tab: {
      type: Number,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onlyActive: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    openImportModal: false,
    products: [],
    importRequiredAnyOfFields: ['sku', 'ean'],
    importFields: [
      { text: 'SKU', value: 'sku' },
      { text: 'EAN', value: 'ean' },
      { text: 'Nome', value: 'name' },
      { text: 'Preço 1', value: 'price_1' },
      { text: 'Preço 2', value: 'price_2' },
    ],
  }),

  provide: function () {
    return {
      objectSelected: this.formatImportedOffers,
      updateRequiredColumns: () => {},
    }
  },

  methods: {
    async formatImportedOffers({ objects, selectedColumns }) {
      let idField = null
      let ids = []

      if (selectedColumns.includes('sku')) {
        idField = 'sku'
        ids = objects.map(({ sku }) => sku)
      } else if (selectedColumns.includes('ean')) {
        idField = 'ean'
        ids = objects.map(({ ean }) => ean)
      } else {
        this.$alert({
          title: 'Erro na importação',
          message: 'O arquivo importado precisa conter SKU ou EAN',
        })
        return
      }
      ids = ids.filter((item) => item !== '')

      const payload = {}
      payload[`${idField}s`] = ids

      if (this.onlyActive) {
        payload.active = true
      }

      await this.$apollo
        .query({
          query: QUERY_VALIDATE_IMPORTED_PRODUCTS,
          variables: payload,
        })
        .then((response) => {
          this.found = response.data.products.products
          let allValues = []
          this.found.forEach((prod) => {
            allValues = [...allValues, ...flatten(prod.eans), prod.ean, prod.sku]
          })
          this.not_found = ids.filter((item) => {
            return allValues.indexOf(item) === -1
          })
        })
      let confirmed = true
      if (this.not_found.length) {
        confirmed = await this.$confirm({
          message: `Os seguintes ${this.not_found.length} produto(s) não foram encontrados:\n ${this.not_found.join('\n')}
          \n Deseja continuar importação?`,
          confirmText: 'Continuar',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (!confirmed) {
        return
      }

      const checkedProducts = this.found.reduce((acc, product) => {
        objects.forEach((object) => {
          if (product.ean === object[idField] || product.sku === object[idField] || product.eans.indexOf(object[idField]) !== -1) {
            object[idField] = product[idField]
          }
        })
        return { ...acc, [product[idField]]: product }
      }, {})
      const productsToInsert = []
      const insertedIds = []

      objects.forEach((product, index) => {
        if (checkedProducts[product[idField]]) {
          productsToInsert.push({
            imported: true,
            active: true,
            price_1: this.formatToPrice(product.price_1),
            price_2: this.formatToPrice(product.price_2),
            additional_points_rate: product.additional_points_rate ? parseInt(product.additional_points_rate.replace(',', '.')) : 0,
            maximum_per_client: product.maximum_per_client || 0,
            minimum_per_client: product.minimum_per_client || 0,
            name: product.name || checkedProducts[product[idField]].name,
            sku: checkedProducts[product[idField]].sku,
            ean: checkedProducts[product[idField]].ean,
            eans: checkedProducts[product[idField]].eans.length === 1 ? [checkedProducts[product[idField]].ean] : checkedProducts[product[idField]].eans,
            original_eans: [],
            product: {
              ean: checkedProducts[product[idField]].ean,
              eans: checkedProducts[product[idField]].eans.length > 1 ? checkedProducts[product[idField]].eans : [checkedProducts[product[idField]].ean],
            },
          })
        }
        insertedIds.push(product[idField])
      })

      let clonedProducts = cloneDeep(this.products)
      const allProducts = clonedProducts.concat(productsToInsert).reduce((products, product) => {
        return products.some((p) => (p.sku && p.sku === product.sku) || (p._id && p._id === product._id)) ? products : [...products, product]
      }, [])
      this.searchProducts = allProducts
      this.campaign.offer_sales = allProducts
      this.$emit('update:products', allProducts)
    },

    formatToPrice(value) {
      if (!value) return 0
      const formattedValue = value.replace(/[R$\s]/g, '').replace(',', '.')
      return parseFloat(formattedValue) || 0
    },

    setGroup(group, removeList, pushList) {
      const itemGroup = find(removeList, {
        _id: group._id,
      })

      if (!itemGroup) return
      const itemGroupIndex = findIndex(removeList, itemGroup)
      const { products, name, ...groupInfo } = removeList[itemGroupIndex]
      const productsFormatted = products.map((product) => ({ isGroup: true, nameGroup: name, groupInfo, ...product }))

      removeList.splice(itemGroupIndex, 1)
      pushList.push(...productsFormatted)
    },

    setItem(item, removeList, pushList) {
      const { sku } = item
      const itemObject = find(removeList, {
        sku,
      })
      if (!itemObject) return
      const itemObjectIndex = findIndex(removeList, itemObject)
      removeList.splice(itemObjectIndex, 1)

      if (!itemObject.isGroup) pushList.sort((a, b) => this.sortList(a, b)).push(itemObject)
    },

    sortList(a, b) {
      if (a.name > b.name) return 1
      if (a.name < b.name) return -1
      return 0
    },

    removeGroup(group, removeList, pushList) {
      this.productsGroups.push(group)
      group.products.forEach((product) => {
        this.setItem(product, removeList, pushList)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.link-csv-import,
.link-csv-import > i {
  gap: 6px;
  color: $marca-0-l40;
  font-weight: 500;
  cursor: pointer;
}

.selected-products-section {
  border-left: 1px solid$cinza-l60;
  overflow-y: auto;
  overflow-x: none;
  max-height: 600px;
}

.import-section {
  max-height: 600px;
  margin: 0px 0px;
  margin-top: -20px;
}

.csv-import-section {
  height: 100%;

  div {
    width: 100%;

    p {
      margin: auto;
    }
  }
}

.label-csv-import {
  font-size: 13px;
  color: $cinza-l10;
}
</style>
